import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import DropdownSelect from '../common/DropdownSelect';
import { countryList } from '../../const/const';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import type { DropdownSelectOption, Organisation } from '../../types/types';

type OrganisationEditModalProps = {
  show: boolean;
  organisationId: string | null;
  organisationName?: string | null;
  onHide: () => void;
  onSaved: (organisation: Organisation) => void | Promise<void>;
};

type OrganisationEditFormState = {
  name: string;
  legal_name: string;
  registration_number: string;
  country: string;
};

const initialFormState: OrganisationEditFormState = {
  name: '',
  legal_name: '',
  registration_number: '',
  country: '',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getErrorMessage = (err: any): string => {
  if (
    err.code === '23505' ||
    err.message?.includes('organisations_registration_number_key')
  ) {
    return 'This registration number is already registered. If your company already has an account, please contact your administrator or support: info@supplypassport.co';
  }

  return 'An unexpected error occurred. Please try again.';
};

export function OrganisationEditModal({
  show,
  organisationId,
  organisationName,
  onHide,
  onSaved,
}: OrganisationEditModalProps) {
  const [formState, setFormState] =
    useState<OrganisationEditFormState>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof OrganisationEditFormState, string>>
  >({});

  const countryOptions: DropdownSelectOption[] = countryList.map((country) => ({
    id: country.isoCode,
    label: country.countryName,
  }));

  const updateFormField = useCallback(
    <K extends keyof OrganisationEditFormState>(
      field: K,
      value: OrganisationEditFormState[K],
    ) => {
      setFormState((currentState) => ({
        ...currentState,
        [field]: value,
      }));
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [field]: '',
      }));
    },
    [],
  );

  useEffect(() => {
    if (!show || !organisationId) {
      return;
    }

    const fetchOrganisation = async () => {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      const { data, error: fetchError } = await supabase
        .from('organisations')
        .select('id, name, legal_name, registration_number, country')
        .eq('id', organisationId)
        .single();

      if (fetchError) {
        captureSupabaseError(fetchError, { op: 'organisation.fetch-edit' });
        setError(fetchError.message || 'Unable to load organisation.');
        setFormState(initialFormState);
        setLoading(false);
        return;
      }

      setFormState({
        name: data?.name ?? organisationName ?? '',
        legal_name: data?.legal_name ?? '',
        registration_number: data?.registration_number ?? '',
        country: data?.country ?? '',
      });
      setLoading(false);
    };

    void fetchOrganisation();
  }, [organisationId, organisationName, show]);

  const validateForm = () => {
    const nextFieldErrors: Partial<
      Record<keyof OrganisationEditFormState, string>
    > = {};

    if (!formState.name.trim()) {
      nextFieldErrors.name = 'Organisation Name is required';
    }

    if (!formState.registration_number.trim()) {
      nextFieldErrors.registration_number = 'Registration Number is required';
    }

    if (!formState.country.trim()) {
      nextFieldErrors.country = 'Country is required';
    }

    setFieldErrors(nextFieldErrors);
    return Object.keys(nextFieldErrors).length === 0;
  };

  const handleSave = useCallback(async () => {
    if (!organisationId) {
      setError('Organisation not found.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      name: formState.name.trim(),
      legal_name: formState.legal_name.trim() || null,
      registration_number: formState.registration_number.trim(),
      country: formState.country.trim(),
      updated_at: new Date().toISOString(),
    };

    try {
      const { error: updateOrganisationError } = await supabase
        .from('organisations')
        .update({
          ...payload,
          status: 'active',
        })
        .eq('id', organisationId);

      if (updateOrganisationError) {
        throw updateOrganisationError;
      }

      const { error: updateUserOrganisationError } = await supabase
        .from('user_organisations')
        .update({ status: 'active' })
        .eq('organisation_id', organisationId);

      if (updateUserOrganisationError) {
        throw updateUserOrganisationError;
      }

      const { error: updateSupplierError } = await supabase
        .from('suppliers')
        .update({
          company_name: payload.name,
          registration_number: payload.registration_number,
          country: payload.country,
          updated_at: payload.updated_at,
        })
        .eq('id', organisationId);

      if (updateSupplierError) {
        throw updateSupplierError;
      }

      const { data: updatedOrganisation, error: fetchUpdatedError } =
        await supabase
          .from('organisations')
          .select('id, name, legal_name, registration_number, country, status')
          .eq('id', organisationId)
          .single();

      if (fetchUpdatedError) {
        throw fetchUpdatedError;
      }

      await onSaved(updatedOrganisation as Organisation);
    } catch (saveError) {
      captureSupabaseError(saveError, { op: 'organisation.update-settings' });

      setError(getErrorMessage(saveError));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((saveError as any)?.code === '23505') {
        setFieldErrors((currentErrors) => ({
          ...currentErrors,
          registration_number: 'This number is already in use.',
        }));
      }
    } finally {
      setSaving(false);
    }
  }, [
    formState.country,
    formState.legal_name,
    formState.name,
    formState.registration_number,
    onSaved,
    organisationId,
  ]);

  const handleHide = () => {
    if (saving) {
      return;
    }

    setError(null);
    setFieldErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Edit organisation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3" controlId="orgName">
              <Form.Label>Organisation Name</Form.Label>
              <Form.Control
                type="text"
                value={formState.name}
                isInvalid={Boolean(fieldErrors.name)}
                onChange={(event) =>
                  updateFormField('name', event.target.value)
                }
                disabled={saving}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="legalName">
              <Form.Label>Legal Name</Form.Label>
              <Form.Control
                type="text"
                value={formState.legal_name}
                isInvalid={Boolean(fieldErrors.legal_name)}
                onChange={(event) =>
                  updateFormField('legal_name', event.target.value)
                }
                disabled={saving}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.legal_name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="regNumber">
              <Form.Label>Registration Number</Form.Label>
              <Form.Control
                type="text"
                value={formState.registration_number}
                isInvalid={Boolean(fieldErrors.registration_number)}
                onChange={(event) =>
                  updateFormField('registration_number', event.target.value)
                }
                disabled={saving}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.registration_number}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="country">
              <Form.Label>Country</Form.Label>
              <DropdownSelect
                full
                options={countryOptions}
                selectedIds={formState.country}
                onValueChanged={(option) =>
                  updateFormField(
                    'country',
                    (option as DropdownSelectOption).id,
                  )
                }
                disabled={saving}
              />
              {fieldErrors.country && (
                <div className="text-danger small mt-1">
                  {fieldErrors.country}
                </div>
              )}
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleHide}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save organisation'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
